import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, Receipt, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const DEAL_TYPES = ['Upfront Payment', 'Milestone', 'Royalty', 'Option Fee', 'Other'];
const FEE_TYPES = ['Advisory Fee', 'Success Fee', 'Retainer', 'Consulting Fee', 'Other'];
const PAYMENT_STATUSES = ['Pending', 'Invoiced', 'Received', 'Overdue'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'INR'];

const STATUS_COLORS = {
  'Pending': 'bg-amber-100 text-amber-700',
  'Invoiced': 'bg-sky-100 text-sky-700',
  'Received': 'bg-emerald-100 text-emerald-700',
  'Overdue': 'bg-rose-100 text-rose-700',
};

const fmt = (amount, currency) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', minimumFractionDigits: 0 }).format(amount || 0);

function EntryTable({ entries, type, onEdit, onDelete }) {
  const typeLabel = type === 'revenue' ? 'Deal Type' : 'Fee Type';
  const typeField = type === 'revenue' ? 'deal_type' : 'fee_type';
  const emptyIcon = type === 'revenue' ? DollarSign : Receipt;
  const EmptyIcon = emptyIcon;

  if (entries.length === 0) {
    return (
      <div className="text-center py-16">
        <EmptyIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">No {type} entries yet. Click "Add" to get started.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Company</th>
            <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{typeLabel}</th>
            <th className="text-right px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Amount</th>
            <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Status</th>
            <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Date</th>
            <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Notes</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {entries.map((entry) => (
            <tr key={entry.id} className="hover:bg-slate-50/50" data-testid={`${type}-row-${entry.id}`}>
              <td className="px-4 py-3">
                {entry.biotech_company_id ? (
                  <a href={`/biotech/${entry.biotech_company_id}`} className="text-sm font-medium text-sky-700 hover:underline">{entry.company_name || '—'}</a>
                ) : (
                  <span className="text-sm text-slate-600">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-sm text-slate-600">{entry[typeField] || '—'}</td>
              <td className="px-4 py-3 text-sm font-semibold text-slate-900 text-right" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmt(entry.amount, entry.currency)}</td>
              <td className="px-4 py-3">
                <Badge variant="secondary" className={`text-xs ${STATUS_COLORS[entry.payment_status] || 'bg-slate-100 text-slate-600'}`}>{entry.payment_status}</Badge>
              </td>
              <td className="px-4 py-3 text-sm text-slate-500">{entry.date || '—'}</td>
              <td className="px-4 py-3 text-sm text-slate-500 max-w-[200px] truncate">{entry.notes || '—'}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => onEdit(entry)} data-testid={`edit-${type}-${entry.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => onDelete(entry)} className="text-red-500 hover:text-red-700" data-testid={`delete-${type}-${entry.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FinancialsPage() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'fees' ? 'fees' : 'revenue');
  const [revenueEntries, setRevenueEntries] = useState([]);
  const [feeEntries, setFeeEntries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [dialogType, setDialogType] = useState('revenue');
  const [form, setForm] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = async () => {
    try {
      const [revRes, feeRes, compRes] = await Promise.all([
        axios.get(`${API}/revenue`, { withCredentials: true }),
        axios.get(`${API}/fees`, { withCredentials: true }),
        axios.get(`${API}/biotech-companies`, { withCredentials: true }),
      ]);
      setRevenueEntries(revRes.data);
      setFeeEntries(feeRes.data);
      setCompanies(compRes.data?.items || compRes.data || []);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openAdd = (type) => {
    setDialogType(type);
    setEditingId(null);
    setForm({
      biotech_company_id: '',
      [type === 'revenue' ? 'deal_type' : 'fee_type']: type === 'revenue' ? 'Upfront Payment' : 'Advisory Fee',
      amount: '',
      currency: 'USD',
      payment_status: 'Pending',
      date: '',
      notes: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (entry, type) => {
    setDialogType(type);
    setEditingId(entry.id);
    setForm({
      biotech_company_id: entry.biotech_company_id || '',
      [type === 'revenue' ? 'deal_type' : 'fee_type']: type === 'revenue' ? (entry.deal_type || 'Upfront Payment') : (entry.fee_type || 'Advisory Fee'),
      amount: entry.amount?.toString() || '',
      currency: entry.currency || 'USD',
      payment_status: entry.payment_status || 'Pending',
      date: entry.date || '',
      notes: entry.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.biotech_company_id) {
      toast.error('Please select a biotech company');
      return;
    }
    const endpoint = dialogType === 'revenue' ? 'revenue' : 'fees';
    const payload = { ...form, amount: parseFloat(form.amount) || 0 };
    try {
      if (editingId) {
        await axios.put(`${API}/${endpoint}/${editingId}`, payload, { withCredentials: true });
        toast.success(`${dialogType === 'revenue' ? 'Revenue' : 'Fee'} entry updated`);
      } else {
        await axios.post(`${API}/${endpoint}`, payload, { withCredentials: true });
        toast.success(`${dialogType === 'revenue' ? 'Revenue' : 'Fee'} entry added`);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const endpoint = deleteTarget._type === 'revenue' ? 'revenue' : 'fees';
    try {
      await axios.delete(`${API}/${endpoint}/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Entry deleted');
      setDeleteTarget(null);
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const revReceived = revenueEntries.filter(e => e.payment_status === 'Received').reduce((s, e) => s + (e.amount || 0), 0);
  const revPending = revenueEntries.filter(e => e.payment_status !== 'Received').reduce((s, e) => s + (e.amount || 0), 0);
  const feeReceived = feeEntries.filter(e => e.payment_status === 'Received').reduce((s, e) => s + (e.amount || 0), 0);
  const feePending = feeEntries.filter(e => e.payment_status !== 'Received').reduce((s, e) => s + (e.amount || 0), 0);

  const typeOptions = dialogType === 'revenue' ? DEAL_TYPES : FEE_TYPES;
  const typeField = dialogType === 'revenue' ? 'deal_type' : 'fee_type';
  const typeLabel = dialogType === 'revenue' ? 'Deal Type' : 'Fee Type';

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="financials-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Financials</h1>
          <p className="text-base text-slate-500 mt-1" style={{ fontFamily: 'Public Sans, sans-serif' }}>Track deal revenue and service fees</p>
        </div>
        <Button onClick={() => openAdd(activeTab)} className="bg-sky-600 hover:bg-sky-700" data-testid="add-financial-btn">
          <Plus className="w-4 h-4 mr-2" /> Add {activeTab === 'revenue' ? 'Revenue' : 'Fee'}
        </Button>
      </div>

      {/* Combined Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-3.5 h-3.5 text-sky-500" />
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Total Revenue</p>
            </div>
            <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="total-revenue">{fmt(revReceived + revPending, 'USD')}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="w-3.5 h-3.5 text-violet-500" />
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Total Fees</p>
            </div>
            <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="total-fees">{fmt(feeReceived + feePending, 'USD')}</p>
          </CardContent>
        </Card>
        <Card className="border border-emerald-200 shadow-sm bg-emerald-50/30">
          <CardContent className="p-4">
            <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1">Received</p>
            <p className="text-xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="total-received">{fmt(revReceived + feeReceived, 'USD')}</p>
          </CardContent>
        </Card>
        <Card className="border border-amber-200 shadow-sm bg-amber-50/30">
          <CardContent className="p-4">
            <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-1">Outstanding</p>
            <p className="text-xl font-bold text-amber-700" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="total-outstanding">{fmt(revPending + feePending, 'USD')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('revenue')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
            activeTab === 'revenue'
              ? 'text-sky-700'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-revenue"
        >
          <span className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Revenue
            <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-500 ml-1">{revenueEntries.length}</Badge>
          </span>
          {activeTab === 'revenue' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 rounded-t" />}
        </button>
        <button
          onClick={() => setActiveTab('fees')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
            activeTab === 'fees'
              ? 'text-sky-700'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-fees"
        >
          <span className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Fees
            <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-500 ml-1">{feeEntries.length}</Badge>
          </span>
          {activeTab === 'fees' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 rounded-t" />}
        </button>
      </div>

      {/* Table */}
      <Card className="border border-slate-200 shadow-sm" data-testid={`${activeTab}-table`}>
        <CardContent className="p-0">
          {activeTab === 'revenue' ? (
            <EntryTable
              entries={revenueEntries}
              type="revenue"
              onEdit={(e) => openEdit(e, 'revenue')}
              onDelete={(e) => setDeleteTarget({ ...e, _type: 'revenue' })}
            />
          ) : (
            <EntryTable
              entries={feeEntries}
              type="fee"
              onEdit={(e) => openEdit(e, 'fees')}
              onDelete={(e) => setDeleteTarget({ ...e, _type: 'fees' })}
            />
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="financial-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingId ? 'Edit' : 'Add'} {dialogType === 'revenue' ? 'Revenue' : 'Fee'} Entry
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-medium text-slate-500">Biotech Company *</Label>
              <Select value={form.biotech_company_id} onValueChange={(v) => setForm(f => ({ ...f, biotech_company_id: v }))}>
                <SelectTrigger data-testid="financial-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-500">{typeLabel}</Label>
                <Select value={form[typeField] || ''} onValueChange={(v) => setForm(f => ({ ...f, [typeField]: v }))}>
                  <SelectTrigger data-testid="financial-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {typeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">Payment Status</Label>
                <Select value={form.payment_status} onValueChange={(v) => setForm(f => ({ ...f, payment_status: v }))}>
                  <SelectTrigger data-testid="financial-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label className="text-xs font-medium text-slate-500">Amount</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" data-testid="financial-amount" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm(f => ({ ...f, currency: v }))}>
                  <SelectTrigger data-testid="financial-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-500">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} data-testid="financial-date" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-500">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" data-testid="financial-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-sky-600 hover:bg-sky-700" data-testid="save-financial-btn">{editingId ? 'Update' : 'Add'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {deleteTarget?._type === 'revenue' ? deleteTarget?.deal_type : deleteTarget?.fee_type} entry of {fmt(deleteTarget?.amount, deleteTarget?.currency)}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-financial">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
